/**
 * WindParticlesController — production controller for the animated wind
 * particle layer.
 *
 * Shipped 2026-05-20 (commit 3 of the wind-particles feature).
 *
 * Subscribes to:
 *   layers.wind          (visibility toggle)
 *   windAltitudeFt       (altitude slider)
 *   windForecastHour     (±24h scrubber)
 *   windHideOnLowEnd     (commit-4 escape hatch for slow devices)
 *
 * On mount or any of those changing, fetches the right PNG via
 * /api/wind-raster?alt=&hour= and installs / updates the WindParticleLayer
 * on the map.
 *
 * Effect-only component — returns null.
 */
import { useEffect, useRef, useState } from 'react';
import maplibre from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useWindPrefsStore } from '../../stores/windPrefsStore';
import { useAirborneStore } from '../../stores/airborneStore';
import { getMapInstance } from '../MapView';
import { WindParticleLayer } from '../wind/windParticleLayer';
import { WindFillLayer } from '../wind/windFillLayer';

const LAYER_ID = 'wind-particles';
const FILL_LAYER_ID = 'wind-fill';

// Low-end heuristic: if the device reports <4 GB memory (Chrome / Edge
// expose this via navigator.deviceMemory) AND the user has opted in to
// hideOnLowEnd, we skip the layer entirely. iPhones don't expose memory
// but report a small `hardwareConcurrency`; we also bail on that.
function isLowEnd(): boolean {
  if (typeof navigator === 'undefined') return false;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (mem !== undefined && mem < 4) return true;
  if (navigator.hardwareConcurrency !== undefined && navigator.hardwareConcurrency < 4) return true;
  return false;
}

// Detect mobile (iOS / Android) so we can scale particle count.
// 60,000 particles × device-pixel-ratio-2-or-3 trail buffers is way too
// much fillrate for a phone GPU — stutters and burns battery. 20,000 still
// looks lush at phone screen size.
function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  // userAgentData first (modern), fall back to UA string.
  const uaData = (navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  }).userAgentData;
  if (uaData?.mobile === true) return true;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile|Silk|webOS|Opera Mini|IEMobile/i.test(ua);
}

async function loadWindPng(alt: number, hour: number, signal: AbortSignal): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const cleanup = () => signal.removeEventListener('abort', onAbort);
    const onAbort = () => {
      img.src = ''; // cancel pending load
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort);
    img.onload = () => { cleanup(); resolve(img); };
    img.onerror = () => { cleanup(); reject(new Error(`wind-raster load failed for alt=${alt} hour=${hour}`)); };
    img.src = `/api/wind-raster?alt=${alt}&hour=${hour}`;
  });
}

export function WindParticlesController() {
  const visible = useLayerVisibilityStore((s) => s.layers.wind);
  const altitude = useWindPrefsStore((s) => s.windAltitudeFt);
  const forecastHour = useWindPrefsStore((s) => s.windForecastHour);
  const hideOnLowEnd = useWindPrefsStore((s) => s.windHideOnLowEnd);
  // Audit 2026-05-22 CR-1: auto-disable in the air. Wind particles are
  // a pre-flight planning aid; in flight they burn battery and GPU for
  // zero operational value. Pilot can opt out via Settings (the
  // windAutoOffAirborne pref).
  const autoOffAirborne = useWindPrefsStore((s) => s.windAutoOffAirborne);
  const airborneState = useAirborneStore((s) => s.state);
  const inFlight = airborneState === 'armed' || airborneState === 'airborne';

  const layerRef = useRef<WindParticleLayer | null>(null);
  const fillLayerRef = useRef<WindFillLayer | null>(null);
  const installedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  // Incremented on WebGL context restore to force the main effect to
  // reinstall both layers with fresh GL objects.
  const [contextRestoreCount, setContextRestoreCount] = useState(0);

  // Stable ref to the "install layers + fetch PNG" action so both the
  // styledata handler and the context-restore handler can call it without
  // capturing stale closures.  Updated on every render so it always sees
  // the latest altitude / forecastHour values.
  const installAndFetchRef = useRef<(() => void) | null>(null);
  installAndFetchRef.current = () => {
    const map = getMapInstance();
    if (!map) return;

    const pickBeforeId = (): string | undefined => {
      try {
        const layers = map.getStyle()?.layers ?? [];
        const preferred = ['flight-plan-line', 'plan-points', 'ownship'];
        for (const id of preferred) {
          if (layers.find((l) => l.id === id)) return id;
        }
        for (const l of layers) {
          if (l.type !== 'raster' && l.id !== 'wind-particles') return l.id;
        }
      } catch { /* swallow */ }
      return undefined;
    };

    const mobile = isMobile();
    const fill = new WindFillLayer({ alpha: 0.45 });
    fillLayerRef.current = fill;
    try {
      map.addLayer(fill as unknown as maplibre.LayerSpecification, pickBeforeId());
    } catch (e) {
      console.error('[WindFill] addLayer failed:', e);
      fillLayerRef.current = null;
    }

    const layer = new WindParticleLayer({ particleCount: mobile ? 800 : 1500 });
    layerRef.current = layer;
    try {
      map.addLayer(layer as unknown as maplibre.LayerSpecification, pickBeforeId());
      installedRef.current = true;
    } catch (e) {
      console.error('[WindParticles] addLayer failed:', e);
      layerRef.current = null;
      return;
    }

    // Fetch and push the current PNG into the fresh layer instances.
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    // Capture current values — installAndFetchRef.current is always fresh.
    const alt = altitude;
    const hour = forecastHour;
    (async () => {
      try {
        const img = await loadWindPng(alt, hour, ac.signal);
        if (ac.signal.aborted) return;
        if (layerRef.current)     layerRef.current.setWindImage(img);
        if (fillLayerRef.current) fillLayerRef.current.setWindImage(img);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          console.warn('[WindParticles] install PNG fetch failed:', e);
        }
      }
    })();
  };

  // Install / remove the layer based on visibility + low-end gating +
  // airborne auto-off (CR-1). contextRestoreCount forces a reinstall after
  // a WebGL context loss + restore cycle.
  useEffect(() => {
    const shouldRun = visible && !(hideOnLowEnd && isLowEnd()) && !(autoOffAirborne && inFlight);
    const map = getMapInstance();
    if (!map) return;

    if (shouldRun && !installedRef.current) {
      installAndFetchRef.current?.();
    } else if (!shouldRun && installedRef.current) {
      try { map.removeLayer(LAYER_ID); } catch { /* layer wasn't there */ }
      try { map.removeLayer(FILL_LAYER_ID); } catch { /* fill wasn't there */ }
      installedRef.current = false;
      layerRef.current = null;
      fillLayerRef.current = null;
      if (abortRef.current) abortRef.current.abort();
    }
  }, [visible, hideOnLowEnd, autoOffAirborne, inFlight, contextRestoreCount]);

  // Register the styledata handler ONCE (not inside the deps-heavy effect).
  // Using a ref-based dispatcher so the handler always sees the latest
  // shouldRun / altitude / forecastHour without re-registering on every
  // slider nudge. The brief gap caused by constant re-registration was
  // losing styledata events during basemap switches, leaving the layers
  // permanently removed.
  const shouldRunRef = useRef(false);
  shouldRunRef.current = visible && !(hideOnLowEnd && isLowEnd()) && !(autoOffAirborne && inFlight);

  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    const onStyleData = () => {
      if (!shouldRunRef.current) return;
      if (!map.getLayer(LAYER_ID) || !map.getLayer(FILL_LAYER_ID)) {
        // setStyle() wiped our custom layers — clean up remnants and reinstall.
        installedRef.current = false;
        try { map.removeLayer(LAYER_ID); } catch { /* */ }
        try { map.removeLayer(FILL_LAYER_ID); } catch { /* */ }
        layerRef.current = null;
        fillLayerRef.current = null;
        installAndFetchRef.current?.();
      }
    };
    map.on('styledata', onStyleData);
    return () => { map.off('styledata', onStyleData); };
  }, []);

  // WebGL context loss recovery. On iOS Safari / low-memory Android the
  // browser can discard the WebGL context while the tab is backgrounded.
  // Neither MapLibre nor our custom layers recover automatically in all
  // versions. We listen on the canvas directly and force a full reinstall
  // by incrementing contextRestoreCount — which is in the main effect's
  // deps array, causing it to re-run and call installAndFetchRef.
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    const canvas = map.getCanvas();

    const onContextLost = (e: Event) => {
      // preventDefault() asks the browser to restore the context
      // automatically once resources free up (vs. permanent loss).
      e.preventDefault();
      // Null our refs so the main effect knows to reinstall on restore.
      installedRef.current = false;
      layerRef.current = null;
      fillLayerRef.current = null;
      if (abortRef.current) abortRef.current.abort();
    };
    const onContextRestored = () => {
      if (!shouldRunRef.current) return;
      // Bump the counter — triggers the main install effect to re-run.
      setContextRestoreCount((n) => n + 1);
    };

    canvas.addEventListener('webglcontextlost', onContextLost);
    canvas.addEventListener('webglcontextrestored', onContextRestored);
    return () => {
      canvas.removeEventListener('webglcontextlost', onContextLost);
      canvas.removeEventListener('webglcontextrestored', onContextRestored);
    };
  }, []);

  // Fetch + install PNG when alt or hour changes (and layer is live)
  useEffect(() => {
    if (!installedRef.current || !layerRef.current) return;
    const ac = new AbortController();
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = ac;
    (async () => {
      try {
        const img = await loadWindPng(altitude, forecastHour, ac.signal);
        if (ac.signal.aborted) return;
        if (layerRef.current) layerRef.current.setWindImage(img);
        if (fillLayerRef.current) fillLayerRef.current.setWindImage(img);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.warn('[WindParticles] PNG load failed:', e);
      }
    })();
    return () => ac.abort();
  }, [altitude, forecastHour, visible]);

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      const map = getMapInstance();
      if (map && installedRef.current) {
        try { map.removeLayer(LAYER_ID); } catch { /* ignore */ }
        try { map.removeLayer(FILL_LAYER_ID); } catch { /* ignore */ }
      }
      if (abortRef.current) abortRef.current.abort();
      installedRef.current = false;
      layerRef.current = null;
      fillLayerRef.current = null;
    };
  }, []);

  return null;
}
